import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEligibilityEvaluation,
  fetchEligibilityHcpcs
} from "actions/patient-outreaches";
import { selectors } from "reducers/patient-outreaches";
import "./eligibility-detail.scss";

const EligibilityDetail = ({ patientGuid }) => {
  const dispatch = useDispatch();
  const evaluation = useSelector(selectors.getCurrentEligibilityEvaluation);
  const isFetching = useSelector(selectors.getIsFetchingEvaluation);
  const hcpcs = useSelector(selectors.getCurrentHcpcsEligibility);
  const isFetchingHcpcs = useSelector(selectors.getIsFetchingHcpcs);

  // Fire both fetches in parallel. The main eligibility data renders fast; the
  // HCPCS section shows its own spinner while the slower calc completes.
  useEffect(() => {
    if (patientGuid) {
      dispatch(fetchEligibilityEvaluation(patientGuid));
      dispatch(fetchEligibilityHcpcs(patientGuid));
    }
  }, [patientGuid, dispatch]);

  if (!patientGuid) return null;
  if (isFetching) {
    return (
      <div className="eligibility-detail eligibility-detail--loading">
        Loading eligibility…
      </div>
    );
  }
  if (!evaluation) return null;

  if (!evaluation.patientFound) {
    return (
      <div className="eligibility-detail eligibility-detail--not-found">
        <h2>Patient not found</h2>
        <p>{evaluation.notFoundReason}</p>
      </div>
    );
  }

  return (
    <div className="eligibility-detail">
      <header className="eligibility-detail__header">
        <h2>
          Eligibility for {evaluation.patientName || evaluation.patientGuid}
        </h2>
        <p>
          <strong>Overall eligible:</strong>{" "}
          {evaluation.overallEligible ? "✓ Yes" : "✗ No"}
          {evaluation.currentlyInTable && (
            <span className="eligibility-detail__badge">
              Loaded into today&apos;s outreach table
            </span>
          )}
        </p>
      </header>

      <section>
        <h3>Eligibility rules ({evaluation.ruleResults.length})</h3>
        <ol className="rule-list">
          {evaluation.ruleResults.map(r => (
            <li
              key={r.code}
              className={`rule ${r.passed ? "rule--pass" : "rule--fail"}`}
            >
              <span className="rule__status">{r.passed ? "✓" : "✗"}</span>
              <span className="rule__name">{r.displayName}</span>
              {r.detail && (
                <p
                  className={`rule__detail ${
                    r.passed ? "rule__detail--pass" : "rule__detail--fail"
                  }`}
                >
                  {r.detail}
                </p>
              )}
            </li>
          ))}
        </ol>
      </section>

      {evaluation.informationalNotes &&
        evaluation.informationalNotes.length > 0 && (
          <section>
            <h3>Informational notes</h3>
            <ul className="notes-list">
              {evaluation.informationalNotes.map(n => (
                <li key={n.code} className="note">
                  <strong>{n.displayName}:</strong> {n.message}
                </li>
              ))}
            </ul>
          </section>
        )}

      <HcpcsSection hcpcs={hcpcs} isFetching={isFetchingHcpcs} />

      {evaluation.currentRow && (
        <section>
          <h3>Today&apos;s outreach row details</h3>
          <dl className="current-row">
            <dt>Allocated to</dt>
            <dd>
              {evaluation.currentRow.allocatedCoachName ||
                evaluation.currentRow.allocatedCoachGuid ||
                "—"}
            </dd>
            <dt>Outreach state</dt>
            <dd>{evaluation.currentRow.outreachState}</dd>
            <dt>Date loaded</dt>
            <dd>{evaluation.currentRow.dtDateLoaded}</dd>
          </dl>
        </section>
      )}
    </div>
  );
};

const formatDate = value => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
};

EligibilityDetail.propTypes = {
  patientGuid: PropTypes.string
};

// Renders the HCPCS eligibility section independently of the rest of the lookup.
// Has its own loading / empty / populated branches so this slow-loading section
// doesn't block the rest of the panel from rendering.
const HcpcsSection = ({ hcpcs, isFetching }) => {
  if (isFetching) {
    return (
      <section>
        <h3>HCPCS eligibility</h3>
        <div className="hcpcs-eligibility-table__loading">
          Loading equipment eligibility…
        </div>
      </section>
    );
  }

  // hcpcs is { patientGuid, patientFound, items } from the BE.
  // Items can be null (e.g. patient has no HCPCS history or the calc failed).
  const items = hcpcs?.items;
  if (!items || items.length === 0) {
    return (
      <section>
        <h3>HCPCS eligibility</h3>
        <p className="hcpcs-eligibility-table__empty">
          No HCPCS order history on file.
        </p>
      </section>
    );
  }

  return (
    <section>
      <h3>HCPCS eligibility ({items.length})</h3>
      <table className="hcpcs-eligibility-table">
        <thead>
          <tr>
            <th>Equipment</th>
            <th>Last ordered</th>
            <th>Next eligible</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr
              key={`${item.productType}-${idx}`}
              className={
                item.eligibleToday
                  ? "hcpcs-row hcpcs-row--eligible"
                  : "hcpcs-row hcpcs-row--not-eligible"
              }
            >
              <td>
                <strong>{item.productType}</strong>
                {item.productLine && (
                  <span className="hcpcs-row__product-line">
                    {" "}
                    ({item.productLine})
                  </span>
                )}
                {item.lastOrderedItemName && (
                  <div className="hcpcs-row__item-name">
                    {item.lastOrderedItemName}
                  </div>
                )}
              </td>
              <td>{formatDate(item.lastOrderDate)}</td>
              <td>{formatDate(item.eligibilityDate)}</td>
              <td>
                {item.eligibleToday ? (
                  <span className="hcpcs-row__status hcpcs-row__status--eligible">
                    ✓ Eligible now
                  </span>
                ) : (
                  <span className="hcpcs-row__status hcpcs-row__status--not-eligible">
                    Not eligible yet
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

HcpcsSection.propTypes = {
  hcpcs: PropTypes.shape({
    items: PropTypes.array
  }),
  isFetching: PropTypes.bool
};

export default EligibilityDetail;
